import useProvidedContext from '@/Hooks/UseProvidedContext';
import { createContext, useMemo, type ReactNode } from 'react';

export class EventContextActions {
    subscriptions: [keyof EventTypeMapping, (event: EventData<any>) => void][] = [];

    subscribe = <K extends keyof EventTypeMapping,>(event: K, func: (data?: EventData<EventTypeMapping[K]>) => void): void => {
        const index = this.subscriptions.findIndex((x) => x[0] === event && x[1] === func);
        if (index >= 0) {
            console.warn(`A subscription on event ${event} with func ${func} already exists`);
            return;
        }

        this.subscriptions.push([event, func]);
    };

    unsubscribe = <K extends keyof EventTypeMapping,>(event: K, func: (data?: EventData<EventTypeMapping[K]>) => void): void => {
        const index = this.subscriptions.findIndex((x) => x[0] === event && x[1] === func);
        if (index === -1) {
            console.warn(`No subscription found for ${event} with func ${func}`);
            return;
        }

        this.subscriptions.splice(index, 1);
    };

    emit = <K extends keyof EventTypeMapping,>(event: K, args: EventTypeMapping[K]): void => {
        this.subscriptions
            .filter((x) => x[0] === event)
            .forEach((subscription) => {
                const eventHandler = subscription[1] as (event: EventData<EventTypeMapping[K]>) => void;

                const event = {
                    data: args,
                };

                eventHandler(event);
            });
    };
}

export const EventContext = createContext<EventContextActions | null>(null);

export function EventContextProvider({ children }: Readonly<{ children: ReactNode }>): React.ReactElement {
    const events = useMemo<EventContextActions>(() => new EventContextActions(), []);

    return (
        <EventContext.Provider value={events}>
            {children}
        </EventContext.Provider>
    );
}

export const useEventContext = (): EventContextActions => {
    return useProvidedContext(EventContext);
};

export interface EventData<T> {
    data: T;
}

// -----------------------------------
// Every GlobalEvent key needs a corresponding EventTypeMapping entry to describe which type would be expected as the (optional) arguments
// This would be used in the subscribed function to receive data from the originating "emit" function being called
//
// Example: If you create a GlobalEvent called "MenuItemChanged" and you'd want to emit an event with that MenuItem object as argument: add 'MenuItemChanged': MenuItem to the EventTypeMapping
// When the subscribed function is then being called (on emit of the event), the subscribed function will receive an argument in the form of EventData<MenuItem> ( looks like: { data: { MenuItem } } )
// -----------------------------------
export const GlobalEvent = {
    CustomWorklistsChanged: 'CustomWorklistsChanged',
} as const;

export interface EventTypeMapping {
    CustomWorklistsChanged: null;
}
